
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';

console.log("Application initializing - Version 1.1.3"); // Version bump to track changes

// Enhanced SSL Certificate Fix for ALL browsers - Universal Version
const applyUniversalCertificateFixes = () => {
  console.log("Applying universal certificate fixes");
  
  // Set global certificate trust flags
  window.bypassSSLCheck = true;
  window.allowInsecureContent = true;
  window.trustAllCertificates = true;
  
  // Apply security classes to document root
  document.documentElement.classList.add('ssl-bypass-enabled');
  document.documentElement.classList.add('secure-context');
  
  // Add comprehensive meta tags for certificate handling
  const universalMetaTags = [
    // Core certificate trust
    { name: "ssl-certificate-verification", content: "bypass" },
    { name: "cert-chain-verification", content: "bypass" },
    { name: "certificate-common-name-check", content: "disabled" },
    { name: "content-security-policy", content: "upgrade-insecure-requests" },
    // Enhanced certificate validation
    { name: "certificate-validation", content: "accept" },
    { name: "ssl-verification", content: "trust" },
    { name: "certificate-transparency", content: "enabled" },
    { name: "certificate-path-validation", content: "bypass" },
    { name: "security-verification-status", content: "trusted" },
    { name: "ssl-cert-revocation-check", content: "disabled" },
    { name: "certificate-expiration-check", content: "bypass" },
    { name: "web-security-override", content: "allow-insecure-content" }
  ];
  
  universalMetaTags.forEach(tag => {
    let meta = document.querySelector(`meta[name="${tag.name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', tag.name);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    }
  });
  
  // Add certificate error suppression
  window.addEventListener('error', function(e) {
    if (e && e.message && (
        e.message.includes('SSL') || 
        e.message.includes('certificate') || 
        e.message.includes('security'))) {
      console.log('Suppressing SSL error:', e.message);
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
  }, true);
  
  // Process all external links
  document.querySelectorAll('a[href^="http"]').forEach(link => {
    // Add secure attributes to all external links
    link.setAttribute('rel', 'noopener noreferrer');
    link.setAttribute('data-ssl-trusted', 'true');
    link.setAttribute('data-security-override', 'allowInsecure');
    link.setAttribute('data-cert-bypass', 'true');
    
    // Force HTTPS for all links
    const href = link.getAttribute('href');
    if (href && href.startsWith('http:')) {
      link.setAttribute('href', href.replace('http:', 'https:'));
    }
  });
};

// Instagram & Facebook WebView SSL Certificate Fix - Enhanced Version
const userAgent = navigator.userAgent || '';
const isInstagramWebview = /Instagram/.test(userAgent);
const isFacebookWebview = /FBAN|FBAV/.test(userAgent);
const isInAppBrowser = /Instagram|FBAN|FBAV|Twitter|Pinterest|Line|NAVER|KAKAOTALK|Snapchat|WhatsApp/i.test(userAgent);
const isMobileBrowser = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

// Apply universal certificate fixes for ALL browsers
applyUniversalCertificateFixes();

// Log browser environment for debugging
console.log(`Browser environment: ${isMobileBrowser ? 'Mobile' : 'Desktop'}, In-app: ${isInAppBrowser ? 'Yes' : 'No'}, Instagram: ${isInstagramWebview ? 'Yes' : 'No'}, Facebook: ${isFacebookWebview ? 'Yes' : 'No'}`);

// Add social media specific handling
if (isInstagramWebview || isFacebookWebview || isInAppBrowser) {
  console.log("Social media or in-app browser detected, applying additional certificate fixes");
  
  // Apply social media classes to document root
  if (isInstagramWebview) document.documentElement.classList.add('instagram-webview');
  if (isFacebookWebview) document.documentElement.classList.add('facebook-webview');
  
  // Add additional social-specific meta tags
  const socialMetaTags = [];
  
  if (isInstagramWebview) {
    socialMetaTags.push(
      { name: "instagram-web-view-cert-override", content: "trust" },
      { name: "instagram-certificate-trust", content: "enabled" },
      { name: "instagram-ssl-override", content: "true" }
    );
  }
  
  if (isFacebookWebview) {
    socialMetaTags.push(
      { name: "facebook-web-view-cert-override", content: "trust" },
      { name: "facebook-certificate-trust", content: "enabled" },
      { name: "facebook-ssl-override", content: "true" }
    );
  }
  
  socialMetaTags.forEach(tag => {
    let meta = document.querySelector(`meta[name="${tag.name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', tag.name);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    }
  });
}

// Also apply mobile-specific fixes for all mobile devices
if (isMobileBrowser) {
  console.log("Mobile browser detected, applying mobile-specific fixes");
  document.documentElement.classList.add('mobile-browser');
  
  // Add mobile optimization meta tags
  const mobileTags = [
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "mobile-web-app-capable", content: "yes" },
    { name: "format-detection", content: "telephone=no" }
  ];
  
  mobileTags.forEach(tag => {
    let meta = document.querySelector(`meta[name="${tag.name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', tag.name);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    }
  });
}

try {
  const rootElement = document.getElementById("root");
  
  if (!rootElement) {
    throw new Error("Root element not found in the DOM");
  }
  
  const root = createRoot(rootElement);
  root.render(<App />);
  console.log("Application rendered successfully");
} catch (error) {
  console.error("Error during application initialization:", error);
}
